<template>
  <Transition name="account-creation-modal">
    <div
      v-if="visible"
      class="account-creation-modal-overlay"
      :class="{ 'account-creation-modal-overlay--glass': glass }"
      @click.self="handleCancel"
    >
      <div
        class="account-creation-modal-card"
        :class="{
          'account-creation-modal-card--compact': compact,
          'account-creation-modal-card--glass': glass,
          'account-creation-modal-card--resizable': resizable,
        }"
        @click.stop
      >
        <div
          class="account-creation-modal-header"
          :class="{ 'account-creation-modal-header--glass': glass }"
        >
          <span>{{ compact ? compactTitle : dynamicTitle }}</span>
          <div class="account-creation-modal-icons">
            <button
              type="button"
              class="account-creation-modal-close-btn"
              aria-label="Close"
              @click="handleCancel"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div v-if="compact" class="account-creation-modal-body account-creation-modal-body--compact">
          <div class="account-creation-modal-compact-grid">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Account Type<span class="account-creation-required">*</span></label>
              <select v-model="form.accountType" class="account-creation-modal-input" @change="clearError('accountType')">
                <option v-if="showAccountTypeOption('Expense')" value="Expense">Expense</option>
                <option v-if="showAccountTypeOption('Other Expense')" value="Other Expense">Other Expense</option>
                <option v-if="showAccountTypeOption('Cost of Goods Sold')" value="Cost of Goods Sold">Cost of Goods Sold</option>
                <option v-if="showAccountTypeOption('Income')" value="Income">Income</option>
                <option v-if="showAccountTypeOption('Other Income')" value="Other Income">Other Income</option>
                <option v-if="showAccountTypeOption('Bank')" value="Bank">Bank</option>
                <option v-if="showAccountTypeOption('Credit Card')" value="Credit Card">Credit Card</option>
                <option v-if="showAccountTypeOption('Payroll Liability')" value="Payroll Liability">Payroll Liability</option>
                <option v-if="showAccountTypeOption('Current Liability')" value="Current Liability">Current Liability</option>
              </select>
              <div v-if="errors.accountType" class="account-creation-modal-error">{{ errors.accountType }}</div>
            </div>

            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Account Number<span class="account-creation-required">*</span></label>
              <input
                class="account-creation-modal-input"
                type="text"
                v-model="form.accountNumber"
                readonly
                @input="clearError('accountNumber')"
              />
              <div v-if="errors.accountNumber" class="account-creation-modal-error">{{ errors.accountNumber }}</div>
            </div>

            <div class="account-creation-modal-row account-creation-modal-row--full">
              <label class="account-creation-modal-label">Account Name<span class="account-creation-required">*</span></label>
              <input
                class="account-creation-modal-input"
                type="text"
                v-model="form.accountName"
                placeholder="e.g. Meals and Entertainment"
                @input="clearError('accountName')"
              />
              <div v-if="errors.accountName" class="account-creation-modal-error">{{ errors.accountName }}</div>
            </div>

            <div class="account-creation-modal-row account-creation-modal-row--inline">
              <div
                class="account-creation-modal-checkbox-group account-creation-modal-checkbox-group--compact"
                :class="{ checked: form.active }"
                @click="toggleActive"
              >
                <input type="checkbox" v-model="form.active" @click.stop />
                <label>Active account</label>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="account-creation-modal-body">
        <!-- Account Type Section -->
        <div class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Account Type<span class="account-creation-required">*</span></label>
              <select v-model="form.accountType" class="account-creation-modal-input" @change="clearError('accountType')">
                <optgroup v-if="showAccountTypeOption('Income') || showAccountTypeOption('Other Income')" label="REVENUE & INCOME">
                  <option v-if="showAccountTypeOption('Income')" value="Income">Income</option>
                  <option v-if="showAccountTypeOption('Other Income')" value="Other Income">Other Income</option>
                </optgroup>
                <optgroup v-if="showAccountTypeOption('Expense') || showAccountTypeOption('Other Expense') || showAccountTypeOption('Cost of Goods Sold')" label="EXPENSES & COSTS">
                  <option v-if="showAccountTypeOption('Expense')" value="Expense">Expense</option>
                  <option v-if="showAccountTypeOption('Other Expense')" value="Other Expense">Other Expense</option>
                  <option v-if="showAccountTypeOption('Cost of Goods Sold')" value="Cost of Goods Sold">Cost of Goods Sold (COGS)</option>
                </optgroup>
                <optgroup v-if="showAccountTypeOption('Cash') || showAccountTypeOption('Bank') || showAccountTypeOption('Other current Asset') || showAccountTypeOption('Inventory Asset') || showAccountTypeOption('Other Assets') || showAccountTypeOption('Fixed Asset')" label="ASSETS & RESOURCES">
                  <option v-if="showAccountTypeOption('Cash')" value="Cash">Cash</option>
                  <option v-if="showAccountTypeOption('Bank')" value="Bank">Bank</option>
                  <option v-if="showAccountTypeOption('Other current Asset')" value="Other current Asset">Other current Asset</option>
                  <option v-if="showAccountTypeOption('Inventory Asset')" value="Inventory Asset">Inventory Asset</option>
                  <option v-if="showAccountTypeOption('Other Assets')" value="Other Assets">Other Assets</option>
                  <option v-if="showAccountTypeOption('Fixed Asset')" value="Fixed Asset">Fixed Asset</option>
                </optgroup>
                <optgroup v-if="showAccountTypeOption('Payroll Liability') || showAccountTypeOption('Current Liability') || showAccountTypeOption('Long Term Liability')" label="LIABILITIES & OBLIGATIONS">
                  <option v-if="showAccountTypeOption('Payroll Liability')" value="Payroll Liability">Payroll Liability</option>
                  <option v-if="showAccountTypeOption('Current Liability')" value="Current Liability">Current Liability</option>
                  <option v-if="showAccountTypeOption('Long Term Liability')" value="Long Term Liability">Long Term Liability</option>
                </optgroup>
                <optgroup v-if="showAccountTypeOption('Credit Card') || showAccountTypeOption('Equity') || showAccountTypeOption('Paypal')" label="SPECIAL ACCOUNTS">
                  <option v-if="showAccountTypeOption('Credit Card')" value="Credit Card">Credit Card</option>
                  <option v-if="showAccountTypeOption('Equity')" value="Equity">Equity</option>
                  <option v-if="showAccountTypeOption('Paypal')" value="Paypal">Paypal</option>
                </optgroup>
              </select>
              <div v-if="errors.accountType" class="account-creation-modal-error">{{ errors.accountType }}</div>
            </div>
          </div>
        </div>

        <!-- Basic Information Section -->
        <div class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Account Number<span class="account-creation-required">*</span></label>
              <input 
                class="account-creation-modal-input" 
                type="text" 
                v-model="form.accountNumber"
                readonly
                @input="clearError('accountNumber')"
              />
              <div v-if="errors.accountNumber" class="account-creation-modal-error">{{ errors.accountNumber }}</div>
            </div>

            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Account Name<span class="account-creation-required">*</span></label>
              <input 
                class="account-creation-modal-input" 
                type="text" 
                v-model="form.accountName"
                @input="clearError('accountName')"
              />
              <div v-if="errors.accountName" class="account-creation-modal-error">{{ errors.accountName }}</div>
            </div>
          </div>
        </div>

        <!-- Bank Details Section (conditional) -->
        <div v-if="form.accountType === 'Bank'" class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Bank Name<span class="account-creation-required">*</span></label>
              <input 
                class="account-creation-modal-input" 
                type="text" 
                v-model="form.bankName"
                @input="clearError('bankName')"
              />
              <div v-if="errors.bankName" class="account-creation-modal-error">{{ errors.bankName }}</div>
            </div>

            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Bank Account Type<span class="account-creation-required">*</span></label>
              <select 
                class="account-creation-modal-input"
                v-model="form.bankAccountType"
                @change="clearError('bankAccountType')"
              >
                <option value="">Select Type</option>
                <option value="Checking">Checking</option>
                <option value="Money Market">Money Market</option>
                <option value="Saving">Saving</option>
              </select>
              <div v-if="errors.bankAccountType" class="account-creation-modal-error">{{ errors.bankAccountType }}</div>
            </div>
          </div>

          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Bank Account Number<span class="account-creation-required">*</span></label>
              <input 
                class="account-creation-modal-input" 
                type="text" 
                v-model="form.bankAccountNumber"
                @input="clearError('bankAccountNumber')"
              />
              <div v-if="errors.bankAccountNumber" class="account-creation-modal-error">{{ errors.bankAccountNumber }}</div>
            </div>
          </div>
        </div>

        <!-- Credit Card Details Section (conditional) -->
        <div v-if="form.accountType === 'Credit Card'" class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Credit Limit (USD)</label>
              <input 
                class="account-creation-modal-input" 
                type="number" 
                min="0" 
                step="0.01"
                v-model="form.creditLimit"
              />
            </div>

            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Card Number</label>
              <input 
                class="account-creation-modal-input" 
                type="text" 
                v-model="form.cardOrLoanNumber"
              />
            </div>
          </div>
        </div>

        <!-- PayPal Details Section (conditional) -->
        <div v-if="form.accountType === 'Paypal'" class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">PayPal Email</label>
              <input 
                class="account-creation-modal-input" 
                type="email" 
                v-model="form.paypalEmail"
              />
            </div>
          </div>
        </div>

        <!-- Additional Options Section -->
        <div class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <div class="account-creation-modal-checkbox-group" :class="{ 'checked': form.isSubAccount }" @click="toggleSubAccount">
                <input 
                  type="checkbox" 
                  v-model="form.isSubAccount"
                  @click.stop
                />
                <label>Is Sub-Account</label>
              </div>
            </div>

            <div class="account-creation-modal-row">
              <div class="account-creation-modal-checkbox-group" :class="{ 'checked': form.active }" @click="toggleActive">
                <input 
                  type="checkbox" 
                  v-model="form.active"
                  @click.stop
                />
                <label>Active</label>
              </div>
            </div>
          </div>

          <!-- Parent Account Selection (conditional) -->
          <div v-if="form.isSubAccount" class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Parent Account<span class="account-creation-required">*</span></label>
              <select 
                class="account-creation-modal-input"
                v-model="form.parentAccountId"
                @change="clearError('parentAccount')"
              >
                <option value="">Select Parent Account</option>
                <option v-for="account in parentAccounts" :key="account.id" :value="account.id">
                  {{ account.accountName }} ({{ account.accountNumber }})
                </option>
              </select>
              <div v-if="errors.parentAccount" class="account-creation-modal-error">{{ errors.parentAccount }}</div>
            </div>
          </div>
        </div>

        <!-- Balance Information Section -->
        <div class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Opening Balance (USD)</label>
              <input 
                class="account-creation-modal-input" 
                type="number"
                min="0"
                step="0.01"
                v-model="form.openingBalance"
              />
            </div>

            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">As of</label>
              <DatePicker 
                v-model="form.asOf"
                class="account-creation-modal-input"
                placeholder="MM/DD/YYYY"
              />
            </div>
          </div>
        </div>

        <!-- Comments Section -->
        <div class="account-creation-modal-section">
          <div class="account-creation-modal-form-row">
            <div class="account-creation-modal-row">
              <label class="account-creation-modal-label">Comments</label>
              <textarea 
                class="account-creation-modal-input" 
                v-model="form.comments"
                rows="3"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <div
        class="account-creation-modal-actions"
        :class="{ 'account-creation-modal-actions--glass': glass }"
      >
        <PrimaryButton
          label="Save"
          icon="bi bi-check-lg"
          compact
          :loading="loading"
          @click="handleOk"
        />
        <SecondaryButton
          label="Cancel"
          icon="bi bi-x-lg"
          compact
          :disabled="loading"
          @click="handleCancel"
        />
      </div>

      <span v-if="resizable" class="account-creation-modal-resize-grip" aria-hidden="true">
        <i class="bi bi-arrows-angle-expand"></i>
      </span>
    </div>
  </div>
  </Transition>

  <ErrorModal
    :show="showErrorModal"
    title="Validation Error"
    :message="errorMessage"
    @close="closeErrorModal"
  />

  <SuccessModal
    :show="showSuccessModal"
    title="Success"
    :message="successMessage"
    @close="closeSuccessModal"
  />
</template>

<script>
import '../styles/NewAccountCreationDialog.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import DatePicker from '@/components/DatePicker.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'NewAccountCreationDialog',
  components: { DatePicker, ErrorModal, SuccessModal, PrimaryButton, SecondaryButton },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: [String, Number],
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    editAccountId: {
      type: [String, Number],
      default: null
    },
    defaultAccountType: {
      type: String,
      default: 'Income',
    },
    defaultAccountName: {
      type: String,
      default: '',
    },
    restrictAccountTypes: {
      type: Array,
      default: () => [],
    },
    compact: {
      type: Boolean,
      default: false,
    },
    glass: {
      type: Boolean,
      default: false,
    },
    resizable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        accountType: 'Income',
        accountNumber: '',
        accountName: '',
        bankName: '',
        bankAccountType: '',
        bankAccountNumber: '',
        creditLimit: '',
        cardOrLoanNumber: '',
        paypalEmail: '',
        active: true,
        isSubAccount: false,
        asOf: this.formatDateForDatePicker(new Date()),
        comments: '',
        parentAccountId: '',
        openingBalance: 0.00
      },
      parentAccounts: [],
      existingAccounts: [],
      errors: {},
      loading: false,
      showErrorModal: false,
      errorMessage: '',
      showSuccessModal: false,
      successMessage: '',
      createdAccount: null
    };
  },
  computed: {
    dynamicTitle() {
      return this.isEdit ? 'Edit Account' : `${this.form.accountType} Account`;
    },
    compactTitle() {
      const type = this.form.accountType || this.defaultAccountType || 'Account';
      return `New ${type}`;
    },
    showAccountTypeOption() {
      const restricted = this.restrictAccountTypes || [];
      return (type) => !restricted.length || restricted.includes(type);
    },
  },
  methods: {
    formatDateForDatePicker(date) {
      if (!date) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },
    toggleSubAccount() {
      this.form.isSubAccount = !this.form.isSubAccount;
      if (!this.form.isSubAccount) {
        this.form.parentAccountId = '';
        this.clearError('parentAccount');
      }
    },
    toggleActive() {
      this.form.active = !this.form.active;
    },
    async fetchParentAccounts() {
      try {
        const response = await ApiService.getCompanyAccounts(this.companyId);
        const allAccounts = response.data.accounts || response.data || [];
        this.existingAccounts = allAccounts;
        // Filter out sub-accounts for parent account dropdown
        this.parentAccounts = allAccounts.filter(acc => {
          if (this.isEdit && this.editAccountId) {
            return !acc.isSubAccount && String(acc.id) !== String(this.editAccountId);
          }
          return !acc.isSubAccount;
        });
        
        // Auto-generate account number when accounts are loaded and account type is selected
        if (this.form.accountType && !this.isEdit && (!this.form.accountNumber || this.form.accountNumber.trim() === '')) {
          await this.generateNextAccountNumber();
        }
      } catch (error) {
        logger.error('Error fetching parent accounts:', error);
        this.parentAccounts = [];
        this.existingAccounts = [];
      }
    },
    // Get the starting number range for each account type
    getAccountTypeRange(accountType) {
      const ranges = {
        'Cash': { start: 1000, end: 1009 },
        'Bank': { start: 1010, end: 1019 },
        'Paypal': { start: 1020, end: 1029 },
        'Other current Asset': { start: 1200, end: 1299 },
        'Inventory Asset': { start: 1300, end: 1399 },
        'Other Assets': { start: 1400, end: 1499 },
        'Fixed Asset': { start: 1500, end: 1599 },
        'Credit Card': { start: 1600, end: 1699 },
        'Payroll Liability': { start: 2000, end: 2099 },
        'Current Liability': { start: 2100, end: 2199 },
        'Long Term Liability': { start: 2200, end: 2299 },
        'Equity': { start: 3000, end: 3099 },
        'Income': { start: 4000, end: 4099 },
        'Other Income': { start: 4100, end: 4199 },
        'Expense': { start: 5000, end: 5099 },
        'Other Expense': { start: 5100, end: 5199 },
        'Cost of Goods Sold': { start: 6000, end: 6099 }
      };
      return ranges[accountType] || { start: 1000, end: 9999 };
    },
    // Generate the next available account number for the selected account type
    async generateNextAccountNumber() {
      if (!this.form.accountType || this.isEdit) {
        return; // Don't auto-generate in edit mode or if no account type selected
      }
      
      const range = this.getAccountTypeRange(this.form.accountType);
      
      // Find all existing accounts of this type
      const accountsOfSameType = this.existingAccounts.filter(acc => {
        const accType = acc.accountType || acc.type || '';
        return accType === this.form.accountType;
      });
      
      // Extract account numbers and find the highest one
      const accountNumbers = accountsOfSameType
        .map(acc => {
          const accNum = acc.accountNumber || acc.no || acc.account_number || acc.number || '';
          const num = parseInt(accNum);
          return isNaN(num) ? null : num;
        })
        .filter(num => num !== null && num >= range.start && num <= range.end);
      
      // Find the next available number
      let nextNumber = range.start;
      if (accountNumbers.length > 0) {
        const maxNumber = Math.max(...accountNumbers);
        nextNumber = maxNumber + 1;
        // Ensure it doesn't exceed the range
        if (nextNumber > range.end) {
          // If range is exhausted, start searching for gaps
          nextNumber = this.findNextAvailableInRange(range, accountNumbers);
        }
      }
      
      // Set the account number
      this.form.accountNumber = String(nextNumber);
      logger.log(`Auto-generated account number for ${this.form.accountType}: ${this.form.accountNumber}`);
    },
    // Find the next available number in a range, including gaps
    findNextAvailableInRange(range, usedNumbers) {
      const sortedNumbers = usedNumbers.sort((a, b) => a - b);
      
      // Check for gaps in the sequence
      for (let i = range.start; i <= range.end; i++) {
        if (!sortedNumbers.includes(i)) {
          return i;
        }
      }
      
      // No gaps found, return the end of range
      return range.end;
    },
    validateForm() {
      const errors = {};

      if (!this.form.accountType || this.form.accountType.trim() === '') {
        errors.accountType = 'Account Type is required';
      }

      if (!this.form.accountNumber.trim()) {
        errors.accountNumber = 'Account Number is required';
      }

      if (!this.form.accountName.trim()) {
        errors.accountName = 'Account Name is required';
      }

      if (this.form.accountType === 'Bank') {
        if (!this.form.bankName.trim()) {
          errors.bankName = 'Bank Name is required';
        }
        if (!this.form.bankAccountType || this.form.bankAccountType === '') {
          errors.bankAccountType = 'Bank Account Type is required';
        }
        if (!this.form.bankAccountNumber.trim()) {
          errors.bankAccountNumber = 'Bank Account Number is required';
        }
      }

      if (this.form.isSubAccount && !this.form.parentAccountId) {
        errors.parentAccount = 'Parent Account is required for sub-accounts';
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async handleOk() {
      if (!this.validateForm()) {
        const errorMessages = Object.values(this.errors)
          .map(message => `• ${message}`)
          .join('\n');
        this.errorMessage = errorMessages;
        this.showErrorModal = true;
        return;
      }

      this.loading = true;
      try {
        // Clean the form data before sending
        const cleanedForm = { ...this.form };
        
        // Convert empty strings to null for optional fields to avoid enum validation errors
        if (cleanedForm.bankAccountType === '') {
          cleanedForm.bankAccountType = null;
        }
        if (cleanedForm.parentAccountId === '') {
          cleanedForm.parentAccountId = null;
        }
        
        // Convert empty strings to null for numeric fields to avoid numeric validation errors
        if (cleanedForm.openingBalance === '' || cleanedForm.openingBalance === null) {
          cleanedForm.openingBalance = 0;
        } else {
          cleanedForm.openingBalance = parseFloat(cleanedForm.openingBalance) || 0;
        }
        
        if (cleanedForm.creditLimit === '' || cleanedForm.creditLimit === null) {
          cleanedForm.creditLimit = null;
        } else {
          cleanedForm.creditLimit = parseFloat(cleanedForm.creditLimit) || null;
        }
        
        const payload = {
          ...cleanedForm,
          companyId: this.companyId,
          asOf: this.form.asOf
        };

        let response;
        if (this.isEdit) {
          response = await ApiService.put(`/company-account/${this.editAccountId}`, payload);
          this.successMessage = 'Account updated successfully!';
        } else {
          response = await ApiService.post('/company-account/create', payload);
          this.successMessage = 'Account created successfully!';
        }

        this.createdAccount = response.data.account;
        if (this.compact) {
          this.$emit('ok', this.createdAccount);
          this.$emit('close');
          return;
        }
        this.showSuccessModal = true;
      } catch (error) {
        let errorMessage = 'Error creating account';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        this.errorMessage = errorMessage;
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    handleCancel() {
      this.$emit('cancel');
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.$emit('ok', this.createdAccount);
      this.$emit('close');
    },
    resetForm() {
      this.form = {
        accountType: this.defaultAccountType || 'Income',
        accountNumber: '',
        accountName: '',
        bankName: '',
        bankAccountType: '',
        bankAccountNumber: '',
        creditLimit: '',
        cardOrLoanNumber: '',
        paypalEmail: '',
        active: true,
        isSubAccount: false,
        asOf: this.formatDateForDatePicker(new Date()),
        comments: '',
        parentAccountId: '',
        openingBalance: 0.00
      };
      this.errors = {};
      this.createdAccount = null;
      
      // Auto-generate account number after reset if accounts are loaded
      if (this.existingAccounts.length > 0 && this.form.accountType) {
        this.generateNextAccountNumber();
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.form.accountType = this.defaultAccountType || 'Income';
        this.form.accountName = this.defaultAccountName || '';
        this.fetchParentAccounts();
      } else {
        this.resetForm();
      }
    },
    async 'form.accountType'(newType, oldType) {
      // Auto-generate account number when account type changes (only in create mode)
      if (newType && !this.isEdit && newType !== oldType) {
        // Wait for existing accounts to be loaded if not already loaded
        if (this.existingAccounts.length === 0) {
          await this.fetchParentAccounts();
        } else {
          // Always regenerate when account type changes to show next available number for the new type
          // Clear current number first to force regeneration
          this.form.accountNumber = '';
          await this.generateNextAccountNumber();
        }
      }
      
      // Clear related fields when account type changes
      if (this.form.accountType !== 'Bank') {
        this.form.bankName = '';
        this.form.bankAccountType = '';
        this.form.bankAccountNumber = '';
        this.clearError('bankName');
        this.clearError('bankAccountType');
        this.clearError('bankAccountNumber');
      }
      if (this.form.accountType !== 'Credit Card') {
        this.form.creditLimit = '';
        this.form.cardOrLoanNumber = '';
      }
      if (this.form.accountType !== 'Paypal') {
        this.form.paypalEmail = '';
      }
    }
  }
};
</script>
